/**
 * PSR-3 (row 0.99292) — Theme Deep Dives extractor.
 *
 * Pure, deterministic helper that classifies a flat
 * `PsrPublicSentimentEntry[]` into 3-5 dominant themes for Section 4
 * of the Public Sentiment Report.
 *
 * Design contract:
 *   - Pure (no I/O, no time, no randomness) — same inputs always
 *     produce same outputs.
 *   - Conservative — themes with fewer than `THEME_MIN_SAMPLE_COUNT`
 *     observations are dropped. This mirrors PSE-3's
 *     `PSE_OPPOSITIONAL_THRESHOLD` floor: do not advise on a theme that
 *     hasn't been observed at meaningful volume.
 *   - Returns up to `THEME_MAX_COUNT` themes ranked by a recency-
 *     weighted magnitude score (recent + polarised observations rank
 *     above old + neutral observations).
 *   - Every numeric output flows through `Number.isFinite()` — a single
 *     mis-shaped entry cannot poison the result.
 *   - `dominantTiers` is derived from the entries' `stakeholderGroup`
 *     by mapping each known group label onto one of the six PSR-2
 *     stakeholder tiers (federal / state / local / community / tribal /
 *     media). Unknown groups are bucketed into 'community' (the most
 *     permissive default).
 *
 * Taxonomy: 12 themes covering the dominant public-discourse channels
 * for BTM data-center siting. Each theme owns a regex pattern over the
 * combined `text` field of the entry (which carries the
 * `claim`/`sourceSpan` content for image-observation entries, and the
 * 1-3 sentence summary for the other three sources).
 *
 * The regex patterns are intentionally simple — substring matches over
 * lowercased text rather than a full NLP classifier. This keeps the
 * function purely deterministic and unit-testable. A future PSR
 * iteration may upgrade to an embeddings-based classifier; the public
 * shape of this module is the contract callers wire against.
 */
import type { PsrPublicSentimentEntry } from '../publicSentimentOrchestrator';

/* ── Public types ──────────────────────────────────────────────────── */

/**
 * The six stakeholder tiers PSR-2 Section 2 (Stakeholder Map) emits.
 * Re-declared here as a local literal union so the extractor doesn't
 * pull in a heavier dependency. Tier order is the renderer's display
 * order.
 */
export type PsrStakeholderTier =
  | 'federal'
  | 'state'
  | 'local'
  | 'community'
  | 'tribal'
  | 'media';

/**
 * Intensity-trend label per theme. Derived from the slope of the
 * monthly observation count in the trend window:
 *   - 'rising'  — recent half-window count > older half-window count by
 *                 ≥ 25 %.
 *   - 'falling' — recent half-window count < older half-window count by
 *                 ≥ 25 %.
 *   - 'steady'  — within ±25 % band, or fewer than 4 observations total.
 *
 * The 25 % band is chosen to mirror PSR-3's trend-inflection threshold
 * (±0.25 sentiment-score delta — see buildSentimentTrend.ts). The label
 * is presentation-grade only; the renderer treats it as a chip.
 */
export type ThemeIntensityTrend = 'rising' | 'steady' | 'falling';

/**
 * Per-theme result the extractor emits. The Section 4 renderer consumes
 * this directly — `name` is the theme display label, `prevalence` is
 * the percentage share of the entry pool (0-100 integer), `samples[]`
 * are the entries that matched the theme, `dominantTiers[]` is the
 * ranked list of stakeholder tiers driving the theme, `geoCenter` is
 * the geographic concentration label (or null when not derivable from
 * the entry text), and `intensityTrend` is the rising/steady/falling
 * label per the slope rule above.
 */
export interface ThemeResult {
  /** Display label — e.g. "Water Use", "Property Values". */
  name: string;
  /**
   * Percentage share of the input pool (integer 0-100). Always
   * `round(matched / totalEntries × 100)`; clamped to [0, 100].
   */
  prevalence: number;
  /**
   * Ranked list of stakeholder tiers driving this theme — most
   * populous first. Empty array when no entries had a
   * `stakeholderGroup` field.
   */
  dominantTiers: PsrStakeholderTier[];
  /**
   * The PsrPublicSentimentEntry rows that matched this theme. Sorted
   * by `observedAt` descending (most recent first) so the renderer can
   * surface the illustrative quote from `samples[0]` without further
   * sorting.
   */
  samples: PsrPublicSentimentEntry[];
  /**
   * Geographic concentration label — extracted from the first matched
   * entry's text via a simple proper-noun pattern. Null when no
   * geographic signal could be derived. The renderer falls through to
   * "geographic concentration not derivable" when this is null.
   */
  geoCenter: string | null;
  /** Intensity-trend label per the slope rule above. */
  intensityTrend: ThemeIntensityTrend;
}

/* ── Public constants ──────────────────────────────────────────────── */

/**
 * Floor on the per-theme sample count. A theme with fewer than this
 * many matched entries is excluded from the result. Mirrors the
 * PSE-3 `PSE_OPPOSITIONAL_THRESHOLD` discipline: do not surface a
 * signal that hasn't been observed at meaningful volume.
 */
export const THEME_MIN_SAMPLE_COUNT = 5;

/**
 * Maximum number of themes the extractor returns. PSR Section 4 is
 * scoped to "3-5 dominant themes" per the prompt-pack contract; the
 * extractor surfaces the top 5 by magnitude. A higher cap would dilute
 * the section's signal.
 */
export const THEME_MAX_COUNT = 5;

/**
 * Recency window for the trend slope. The extractor splits the input
 * pool into "recent half" (within this many days of the most recent
 * observation) and "older half" (older than that) and compares the
 * counts to label the trend. 90 days mirrors the PSE feasibility-embed
 * window default.
 */
export const THEME_RECENCY_HALF_WINDOW_DAYS = 90;

/* ── Theme taxonomy ────────────────────────────────────────────────── */

/**
 * One taxonomy row. `name` is the display label, `patterns` is a list
 * of substring tokens we match (lowercased) against the combined
 * `text` field. A pattern hit on ANY token marks the entry as
 * belonging to the theme. An entry may belong to multiple themes (a
 * council-minutes quote about "water use and property values" hits
 * both); the extractor counts it under every theme that matches.
 *
 * Theme list (12) covers the dominant public-discourse channels for
 * BTM data-center siting. The list is anchored to the prompt-pack
 * Section 4 reference list ("water use, jobs, traffic, noise, property
 * values, public health, tribal/treaty rights, environmental justice")
 * and extended with four more themes the audit identified
 * (grid impact, tax base, aesthetic, school enrolment).
 */
interface ThemeTaxonomyRow {
  name: string;
  patterns: readonly string[];
}

export const THEME_TAXONOMY: readonly ThemeTaxonomyRow[] = [
  {
    name: 'Water Use',
    patterns: ['water use', 'water consumption', 'cooling water', 'aquifer', 'groundwater', 'water draw', 'water withdrawal', 'wetland'],
  },
  {
    name: 'Jobs',
    patterns: ['jobs', 'employment', 'construction job', 'permanent job', 'workforce', 'hiring', 'living wage'],
  },
  {
    name: 'Traffic',
    patterns: ['traffic', 'truck route', 'congestion', 'road wear', 'haul route', 'access road'],
  },
  {
    name: 'Noise',
    patterns: ['noise', 'decibel', 'cooling tower noise', 'fan noise', 'generator noise', 'humming'],
  },
  {
    name: 'Property Values',
    patterns: ['property value', 'home value', 'real estate', 'property assessment', 'devalue', 'depreciat'],
  },
  {
    name: 'Public Health',
    patterns: ['public health', 'air quality', 'asthma', 'respiratory', 'particulate', 'emissions', 'pollution', 'cancer cluster'],
  },
  {
    name: 'Tribal & Treaty Rights',
    patterns: ['tribal', 'treaty right', 'sovereign nation', 'tribal nation', 'sacred site', 'ancestral land', 'indigenous'],
  },
  {
    name: 'Environmental Justice',
    patterns: ['environmental justice', 'ej community', 'disadvantaged community', 'cumulative impact', 'overburdened'],
  },
  {
    name: 'Grid Impact',
    patterns: ['grid', 'transmission line', 'substation', 'electric rate', 'utility bill', 'load growth', 'reliability'],
  },
  {
    name: 'Tax Base',
    patterns: ['tax base', 'property tax', 'tax abatement', 'pilot agreement', 'tax revenue', 'fiscal impact'],
  },
  {
    name: 'Aesthetic',
    patterns: ['viewshed', 'aesthetic', 'visual impact', 'skyline', 'rural character', 'eyesore', 'industrial appearance'],
  },
  {
    name: 'School Enrolment',
    patterns: ['school enrol', 'school capacity', 'classroom', 'student population', 'school district', 'enrollment'],
  },
] as const;

/* ── Internal helpers ──────────────────────────────────────────────── */

/**
 * Map a free-text stakeholder group label onto one of the six PSR-2
 * tiers. Falls through to 'community' for unknown labels because the
 * community tier is the most permissive bucket (it never implies
 * defamation risk or PII overstep).
 */
function tierForStakeholderGroup(group: string | undefined): PsrStakeholderTier {
  if (!group || typeof group !== 'string') return 'community';
  const g = group.toLowerCase();
  if (/(nrc|ferc|epa|usace|federal|congress|senator|representative|department of)/.test(g)) return 'federal';
  if (/(state|governor|attorney general|puc|psc|public utility commission|public service commission)/.test(g)) return 'state';
  if (/(mayor|council|county|commissioner|planning board|zoning|alderman|supervisor|municipal|township)/.test(g)) return 'local';
  if (/(tribal|tribe|nation|sovereign)/.test(g)) return 'tribal';
  if (/(reporter|journalist|tv|news|times|herald|tribune|gazette|wcsc|wpde|outlet|press|media)/.test(g)) return 'media';
  return 'community';
}

/**
 * Derive a geographic-concentration label from an entry's text via a
 * simple proper-noun + locality-suffix pattern. Returns null when no
 * confident signal is derivable.
 *
 * Heuristics:
 *   - Match `"<Capitalised Word> County"` or `"<Capitalised Word>
 *     Township"` or `"<Capitalised Word>, <ST>"` (two-letter state).
 *   - Take the first hit; if none, return null.
 *
 * Conservative by design: the renderer treats null as "not derivable"
 * and prefers honest gap to a guessed locality.
 */
function deriveGeoCenter(text: string): string | null {
  if (!text || typeof text !== 'string') return null;
  // Pattern A: "<Word> County" / "<Word> Township"
  const a = text.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\s+(County|Township|Parish|Borough)/);
  if (a) return `${a[1]} ${a[2]}`;
  // Pattern B: "<Word>, <ST>" — comma + 2-letter state code
  const b = text.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?),\s*([A-Z]{2})\b/);
  if (b) return `${b[1]}, ${b[2]}`;
  return null;
}

/**
 * Recency-weighted magnitude — the metric we rank themes by.
 *
 *   magnitude = Σ_i ( |polarity_i| × recencyWeight_i )
 *
 * where `recencyWeight_i` is 1.0 for the most recent observation and
 * decays linearly to 0.5 for the oldest observation in the pool (so
 * recency moves the score by up to 2x). This biases the ranking toward
 * themes that have polarised recent observations over themes that
 * dominated older windows but have gone quiet.
 *
 * Returns 0 when the matches array is empty or every polarity is
 * non-finite.
 */
function recencyWeightedMagnitude(
  matches: readonly PsrPublicSentimentEntry[],
  nowMs: number,
  oldestMs: number,
): number {
  if (matches.length === 0) return 0;
  const span = Math.max(1, nowMs - oldestMs);
  let total = 0;
  for (const m of matches) {
    const t = Date.parse(m.observedAt);
    if (!Number.isFinite(t)) continue;
    const ageFrac = Math.max(0, Math.min(1, (nowMs - t) / span));
    const recencyWeight = 1.0 - 0.5 * ageFrac; // 1.0 (newest) → 0.5 (oldest)
    const polarity = Number.isFinite(m.polarity) ? Math.abs(m.polarity) : 0;
    total += polarity * recencyWeight;
  }
  return Number.isFinite(total) ? total : 0;
}

/**
 * Slope-based intensity-trend label. Splits matches into a recent half
 * (within `THEME_RECENCY_HALF_WINDOW_DAYS` of now) and an older half;
 * compares counts; labels 'rising' / 'falling' / 'steady'.
 */
function intensityTrendFor(
  matches: readonly PsrPublicSentimentEntry[],
  nowMs: number,
): ThemeIntensityTrend {
  if (matches.length < 4) return 'steady';
  const halfWindowMs = THEME_RECENCY_HALF_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  let recent = 0;
  let older = 0;
  for (const m of matches) {
    const t = Date.parse(m.observedAt);
    if (!Number.isFinite(t)) continue;
    if (nowMs - t <= halfWindowMs) recent += 1;
    else older += 1;
  }
  if (older === 0 && recent > 0) return 'rising';
  if (recent === 0 && older > 0) return 'falling';
  if (older === 0) return 'steady';
  const ratio = recent / older;
  if (ratio >= 1.25) return 'rising';
  if (ratio <= 0.75) return 'falling';
  return 'steady';
}

/**
 * Rank stakeholder tiers by populated count across the matches.
 */
function rankTiers(matches: readonly PsrPublicSentimentEntry[]): PsrStakeholderTier[] {
  const counts: Record<PsrStakeholderTier, number> = {
    federal: 0, state: 0, local: 0, community: 0, tribal: 0, media: 0,
  };
  let anyTagged = false;
  for (const m of matches) {
    if (m.stakeholderGroup) {
      anyTagged = true;
      const tier = tierForStakeholderGroup(m.stakeholderGroup);
      counts[tier] += 1;
    }
  }
  if (!anyTagged) return [];
  return (Object.keys(counts) as PsrStakeholderTier[])
    .filter(t => counts[t] > 0)
    .sort((a, b) => counts[b] - counts[a]);
}

/* ── Public entry point ────────────────────────────────────────────── */

/**
 * Extract 3-5 dominant themes from a flat
 * `PsrPublicSentimentEntry[]`. Returns an empty array when:
 *   - `entries` is undefined / null / non-array
 *   - `entries.length === 0`
 *   - no theme cleared the `THEME_MIN_SAMPLE_COUNT` floor
 *
 * The function is pure + deterministic + NEVER throws.
 */
export function extractThemes(
  entries: readonly PsrPublicSentimentEntry[] | undefined | null,
): ThemeResult[] {
  if (!Array.isArray(entries) || entries.length === 0) return [];

  // Pass 1: per-theme matches.
  const matchesByTheme = new Map<string, PsrPublicSentimentEntry[]>();
  for (const row of THEME_TAXONOMY) {
    matchesByTheme.set(row.name, []);
  }
  for (const e of entries) {
    if (!e || typeof e.text !== 'string') continue;
    const haystack = e.text.toLowerCase();
    for (const row of THEME_TAXONOMY) {
      if (row.patterns.some(p => haystack.includes(p))) {
        matchesByTheme.get(row.name)!.push(e);
      }
    }
  }

  // Anchor the recency clock to the newest entry in the pool — using
  // wall-clock `Date.now()` would make the function non-deterministic.
  let nowMs = 0;
  let oldestMs = Number.POSITIVE_INFINITY;
  for (const e of entries) {
    const t = Date.parse(e.observedAt);
    if (!Number.isFinite(t)) continue;
    if (t > nowMs) nowMs = t;
    if (t < oldestMs) oldestMs = t;
  }
  if (!Number.isFinite(nowMs) || nowMs === 0) nowMs = 0;
  if (!Number.isFinite(oldestMs)) oldestMs = nowMs;

  // Pass 2: build per-theme result row + filter below the sample floor.
  const total = entries.length;
  const rows: Array<ThemeResult & { magnitude: number }> = [];
  for (const row of THEME_TAXONOMY) {
    const matches = matchesByTheme.get(row.name)!;
    if (matches.length < THEME_MIN_SAMPLE_COUNT) continue;
    // Sort matches by observedAt DESC so samples[0] is the most
    // recent — the renderer surfaces this as the illustrative quote.
    const sortedMatches = [...matches].sort((a, b) => {
      const ta = Date.parse(a.observedAt);
      const tb = Date.parse(b.observedAt);
      const sa = Number.isFinite(ta) ? ta : 0;
      const sb = Number.isFinite(tb) ? tb : 0;
      return sb - sa;
    });
    const prevalenceRaw = (matches.length / total) * 100;
    const prevalence = Number.isFinite(prevalenceRaw)
      ? Math.max(0, Math.min(100, Math.round(prevalenceRaw)))
      : 0;
    const dominantTiers = rankTiers(sortedMatches);
    const geoCenter = deriveGeoCenter(sortedMatches[0]?.text ?? '');
    const intensityTrend = intensityTrendFor(sortedMatches, nowMs);
    const magnitude = recencyWeightedMagnitude(sortedMatches, nowMs, oldestMs);
    rows.push({
      name: row.name,
      prevalence,
      dominantTiers,
      samples: sortedMatches,
      geoCenter,
      intensityTrend,
      magnitude,
    });
  }

  // Rank by magnitude DESC, cap at THEME_MAX_COUNT, strip the
  // internal magnitude field before returning.
  rows.sort((a, b) => b.magnitude - a.magnitude);
  return rows.slice(0, THEME_MAX_COUNT).map(({ magnitude: _m, ...rest }) => {
    void _m;
    return rest;
  });
}
